import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ReactNode } from 'react';
import { useHasPermission } from '../PermissionGate';
import {
  UNPUBLISH_REASON_LABELS,
  EXCHANGE_LABELS,
  POSTING_STATUS_LABELS,
  STATUS,
  TEST_MODE_POSTED_TEXT,
  postedInTestMode,
  storedAccountLabel,
  storedExceptionRequestText,
  storedFleetVariantsText,
  storedDataConfirmationText,
  type Publication,
} from './publicationsApi';

/**
 * Ręczne wycofanie: API zapisuje powód podany przy wycofaniu albo kod
 * `operator`, gdy go nie było — kodu nie pokazujemy (01.10.2026).
 */
const manualWithdrawalText = (reason: string) =>
  reason === 'operator' || reason.trim() === '' ? 'wycofano ręcznie' : `wycofano ręcznie: ${reason}`;

/** Komunikat giełdy bez kropki na końcu — wstawiany w zdanie (bez „..”). */
const withoutFinalDot = (text: string) => text.trim().replace(/\.+$/, '');

/** Kolor żetonu statusu — jeden słownik dla sekcji i kolejki. */
export const statusColor = (
  status: string,
): 'default' | 'success' | 'error' | 'warning' | 'info' => {
  switch (status) {
    case STATUS.PENDING:
      return 'warning';
    case STATUS.POSTED:
      return 'success';
    case STATUS.FAILED:
      return 'error';
    case STATUS.WITHDRAWN:
      return 'info';
    default:
      return 'default';
  }
};

export const PostingStatus = ({ status }: { status: string }) => (
  <Chip size="small" color={statusColor(status)} label={POSTING_STATUS_LABELS[status] ?? status} />
);

export const ExchangeName = ({ exchange }: { exchange: string }) => (
  <strong>{EXCHANGE_LABELS[exchange] ?? exchange}</strong>
);

/**
 * Szczegóły techniczne błędu giełdy (kod HTTP, ścieżka, treść po angielsku)
 * — w dymku przy zdaniu dla operatora, tylko dla administratora
 * (07.10.2026). Operator dostaje samo zdanie z API.
 */
const TechnicalDetails = ({ details }: { details: unknown }) => {
  const isAdmin = useHasPermission('ADMIN_SECTION');
  if (!isAdmin || typeof details !== 'string' || details.trim() === '') return null;
  return (
    <Tooltip title={`Szczegóły techniczne: ${details}`}>
      <InfoOutlinedIcon
        aria-label="szczegóły techniczne"
        fontSize="inherit"
        sx={{ ml: 0.5, verticalAlign: 'text-bottom', cursor: 'help' }}
      />
    </Tooltip>
  );
};

const ErrorLine = ({ children, details }: { children: ReactNode; details?: unknown }) => (
  <Typography variant="caption" color="error.main">
    {children}
    <TechnicalDetails details={details} />
  </Typography>
);

/**
 * Co giełda odpowiedziała i na jakich założeniach oferta została zbudowana.
 *
 * Założenia (np. długość robocza, domyślna zabudowa) mają być widoczne
 * PRZY publikacji, nie dopiero po telefonie przewoźnika. Braki i błędy
 * w całości — planista ma wiedzieć, co poprawić, nie że „coś nie wyszło".
 */
export const PublicationOutcome = ({ publication }: { publication: Publication }) => {
  const result = publication.postingResult ?? {};
  const missing = Array.isArray(result.braki) ? (result.braki as string[]) : [];
  const assumptions = Array.isArray(result.zalozenia) ? (result.zalozenia as string[]) : [];
  // W trybie testowym link „oferta … na giełdzie” udawał prawdziwą
  // publikację — zamiast niego zdanie, że oferty na giełdzie nie widać
  // (07.10.2026).
  const testMode = postedInTestMode(result);
  return (
    <Stack spacing={0.25}>
      {publication.postedLink && !testMode && (
        <Link href={publication.postedLink} target="_blank" rel="noopener noreferrer" variant="body2">
          oferta {publication.exchangeOfferId ?? ''} na giełdzie
        </Link>
      )}
      {publication.status === STATUS.POSTED && testMode && (
        <Box component="span" sx={{ typography: 'caption', color: 'warning.main' }}>
          {TEST_MODE_POSTED_TEXT}
        </Box>
      )}
      {/* Konto oferty zapisane przy publikacji (16.10.2026) — tylko gdy to
          nie zwykłe konto spółki zlecenia. */}
      {storedAccountLabel(result.account) && (
        <Typography variant="caption" color="text.secondary">
          konto: {storedAccountLabel(result.account)}
        </Typography>
      )}
      {/* Fala B (21.10.2026): wyjątek na wniosek i publikacja mimo wariantów floty. */}
      {storedExceptionRequestText(result) && (
        <Typography variant="caption" color="text.secondary">
          {storedExceptionRequestText(result)}
        </Typography>
      )}
      {/* Potwierdzenie danych do publikacji (paczka p. Mariusza 21.10, S-21.10-19). */}
      {storedDataConfirmationText(result.dane_potwierdzone) && (
        <Typography variant="caption" color="text.secondary" data-testid={`posting-data-confirmed-${publication.id}`}>
          {storedDataConfirmationText(result.dane_potwierdzone)}
        </Typography>
      )}
      {storedFleetVariantsText(result.fleetVariantsWarning) && (
        <Typography variant="caption" color="warning.main" data-testid={`posting-fleet-warning-${publication.id}`}>
          {storedFleetVariantsText(result.fleetVariantsWarning)}
        </Typography>
      )}
      {typeof result.error === 'string' && result.error && (
        <ErrorLine details={result.error_details}>{result.error}</ErrorLine>
      )}
      {missing.length > 0 && (
        <Typography variant="caption" color="error.main">
          braki: {missing.join('; ')}
        </Typography>
      )}
      {assumptions.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          założenia: {assumptions.join('; ')}
        </Typography>
      )}
      {typeof result.withdrawn_reason === 'string' && publication.status === STATUS.WITHDRAWN && (
        <Typography variant="caption" color="text.secondary">
          {result.withdrawn_auto === true
            ? `wycofano automatycznie: ${UNPUBLISH_REASON_LABELS[result.withdrawn_reason] ?? result.withdrawn_reason}`
            : manualWithdrawalText(result.withdrawn_reason)}
          {result.delete_from_exchange === 'blad' ? ' (giełda nie potwierdziła wycofania)' : ''}
          {result.delete_from_exchange === 'blad' && <TechnicalDetails details={result.delete_error_details} />}
        </Typography>
      )}
      {/* Zdaniami zamiast „(powód, prób: N): błąd — wycofaj ręcznie” (01.10.2026). */}
      {publication.status === STATUS.POSTED && typeof result.auto_withdraw_error === 'string' && (
        <ErrorLine details={result.auto_withdraw_error_details}>
          Nie udało się automatycznie wycofać oferty (powód:{' '}
          {UNPUBLISH_REASON_LABELS[String(result.auto_withdraw_reason)] ?? String(result.auto_withdraw_reason)};
          liczba prób: {String(result.auto_withdraw_attempts ?? 1)}). Błąd giełdy:{' '}
          {withoutFinalDot(result.auto_withdraw_error)}. Wycofaj ofertę ręcznie.
        </ErrorLine>
      )}
      {typeof result.poprzedni_blad === 'string' && result.poprzedni_blad && publication.status === STATUS.PENDING && (
        <Typography variant="caption" color="text.secondary">
          błąd poprzedniej próby: {result.poprzedni_blad}
        </Typography>
      )}
    </Stack>
  );
};
